import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { loadMarkdown, getManifest } from "@/utils/markdownLoader";
import Loader from "@/components/UI/Loader";
import {
  ArticleHeader,
  ReleaseNote,
  TableOfContents,
} from "@/components/Wiki/WikiComponents";
import {
  formatIsoDate,
  manifestDateValue,
  parseReleaseNoteSections,
  type ReleaseNoteFrontmatter,
  type ReleaseNoteSection,
} from "./releaseNoteMarkdown";
import { scrollToWikiTop } from "../utils/scrollToWikiTop";

type LoadedReleaseNote = {
  slug: string;
  frontmatter: ReleaseNoteFrontmatter;
  sections: ReleaseNoteSection[];
};

type ReleaseNotePageProps = {
  slug: string;
};

function ReleaseNotePage({ slug }: ReleaseNotePageProps) {
  const [current, setCurrent] = useState<LoadedReleaseNote | null>(null);
  const [prevSlug, setPrevSlug] = useState<string | null>(null);
  const [nextSlug, setNextSlug] = useState<string | null>(null);

  useEffect(() => {
    scrollToWikiTop();
  }, [slug]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      const manifest = await getManifest("release-notes");
      const files = (manifest.files ?? [])
        .filter((file: { slug: string }) => file.slug !== "all-release-notes")
        .sort(
          (a: { date?: string }, b: { date?: string }) =>
            manifestDateValue(a.date ?? "") - manifestDateValue(b.date ?? ""),
        );

      const index = files.findIndex(
        (file: { slug: string }) => file.slug === slug,
      );
      const data = await loadMarkdown(`release-notes/${slug}`);
      if (!data) return;

      const sections = parseReleaseNoteSections(data.content);
      if (!mounted) return;

      setCurrent({
        slug,
        frontmatter: data.frontmatter as ReleaseNoteFrontmatter,
        sections,
      });
      setPrevSlug(index > 0 ? files[index - 1].slug : null);
      setNextSlug(
        index >= 0 && index < files.length - 1 ? files[index + 1].slug : null,
      );
    };

    load();
    return () => {
      mounted = false;
    };
  }, [slug]);

  const tocItems = useMemo(
    () =>
      current?.sections.map((section) => ({
        id: section.id,
        label: section.heading,
      })) ?? [],
    [current],
  );

  if (!current) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader type="blue" />
      </div>
    );
  }

  const title =
    current.frontmatter.title?.trim() ||
    `Vivo ${current.frontmatter.releaseVersion || slug}`;
  const description = current.frontmatter.description || "";
  const updated = current.frontmatter.lastUpdated
    ? formatIsoDate(current.frontmatter.lastUpdated)
    : "";

  return (
    <div className="wiki-markdown mx-auto max-w-[calc(100%-72px)] overflow-visible bg-transparent px-9">
      <div className="article-layout overflow-visible">
        <article className="article">
          <Link
            className="article__back"
            to="../all-release-notes"
            relative="path"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5m7-7l-7 7 7 7" />
            </svg>
            All Release Notes
          </Link>

          <ArticleHeader
            eyebrow="VIVO · RELEASE NOTES"
            title={title}
            lede={description}
            type="Release"
            updated={updated}
            author="OmniScience"
          />

          <div className="md article__body">
            {current.sections.map((section) => (
              <div id={section.id} key={section.id}>
                <ReleaseNote tag={section.tag} title={section.heading}>
                  <ul>
                    {section.bullets.map((bullet, index) => (
                      <li key={`${section.id}-${index}`}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {bullet}
                        </ReactMarkdown>
                      </li>
                    ))}
                  </ul>
                </ReleaseNote>
              </div>
            ))}

            <div className="mt-14 flex items-center justify-between border-t border-[#e7eaec] pt-6">
              {prevSlug ? (
                <Link
                  to={`../${prevSlug}`}
                  relative="path"
                  className="text-[14px] font-medium text-[#6b8390] no-underline"
                >
                  ← Previous release
                </Link>
              ) : (
                <span />
              )}
              <Link
                to="../all-release-notes"
                relative="path"
                className="text-[13px] text-[#8fa8b2] no-underline"
              >
                All releases
              </Link>
              {nextSlug ? (
                <Link
                  to={`../${nextSlug}`}
                  relative="path"
                  className="text-[14px] font-medium text-[#6b8390] no-underline"
                >
                  Next release →
                </Link>
              ) : (
                <span />
              )}
            </div>
          </div>
        </article>

        <TableOfContents items={tocItems} />
      </div>
    </div>
  );
}

export default ReleaseNotePage;
